import { useState } from "react";
import { Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions, STUDIO } from "../lib/store";
import { openWhatsApp } from "../lib/whatsapp";
import { fmtDate } from "../lib/format";
import type { Project } from "../lib/types";

/* Personal, revocable links for consultants and contractors — their sheets,
   their RFIs, the dates. No fees, no client details, no other disciplines. */
export function PartnersPanel({ p, canEdit, by }: { p: Project; canEdit: boolean; by: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const list = (p.partners ?? []);
  const link = (t: string) => `https://${STUDIO.domain}/partner/${t}`;

  return (
    <div className="card reveal" style={{ overflow: "hidden", marginTop: 22 }}>
      <div className="row between wrap" style={{ padding: "14px 18px", borderBottom: list.length ? "1px solid var(--line)" : "none", gap: 10 }}>
        <div className="row" style={{ gap: 8 }}><Icon.layers width={16} style={{ color: "var(--accent-ink)" }} /><span className="kicker">Consultant &amp; contractor access</span></div>
        {canEdit && <button className="btn accent sm" onClick={() => setOpen(true)}><Icon.plus width={13} />Give access</button>}
      </div>
      {list.length === 0 ? (
        <div className="dim" style={{ padding: "16px 18px", fontSize: 13, lineHeight: 1.6 }}>
          Give a consultant or contractor a personal link to just their part of the set — the drawings for their discipline, the RFIs that concern them, and the dates. Withdraw it any time.
        </div>
      ) : list.map((t, i) => (
        <div key={t.id} style={{ padding: "13px 18px", borderTop: i ? "1px solid var(--line)" : "none", opacity: t.revoked ? 0.6 : 1 }}>
          <div className="row between wrap" style={{ gap: 10 }}>
            <span className="row" style={{ gap: 10, minWidth: 0, alignItems: "baseline" }}>
              <span className="badge accent mono" style={{ fontSize: 10.5 }}>{t.ref}</span>
              <span style={{ fontSize: 14.5, fontWeight: 600 }}>{t.name}</span>
              <span className="badge" style={{ fontSize: 8.5, textTransform: "capitalize" }}>{t.kind}</span>
              {t.revoked && <span className="badge" style={{ fontSize: 8.5, color: "var(--danger)" }}>withdrawn</span>}
            </span>
            <span className="dim" style={{ fontSize: 11.5 }}>{t.disciplines?.length ? t.disciplines.join(", ") : "All disciplines"} · {fmtDate(t.createdOn)}</span>
          </div>
          {canEdit && !t.revoked && (
            <div className="row wrap" style={{ gap: 8, marginTop: 9 }}>
              <button className="chip" style={{ fontSize: 11 }} onClick={() => { void navigator.clipboard?.writeText(link(t.token)); setCopied(t.id); setTimeout(() => setCopied(null), 1500); }}>{copied === t.id ? "Copied ✓" : "Copy link"}</button>
              <a className="chip" style={{ fontSize: 11 }} href={link(t.token)} target="_blank" rel="noreferrer">Open</a>
              <button className="chip" style={{ fontSize: 11 }} onClick={() => openWhatsApp(`${p.name} — your project access\n\n${link(t.token)}\n\nThis link is personal to you.`)}>WhatsApp</button>
              <span style={{ flex: 1 }} />
              <button className="chip" style={{ fontSize: 11 }} onClick={() => { if (confirm(`Withdraw ${t.name}'s access?`)) actions.revokePartner(p.id, t.id, true); }}>Withdraw</button>
            </div>
          )}
          {canEdit && t.revoked && (
            <div className="row" style={{ gap: 8, marginTop: 9 }}>
              <button className="chip" style={{ fontSize: 11 }} onClick={() => actions.revokePartner(p.id, t.id, false)}>Restore</button>
              <button className="iconbtn" style={{ width: 26, height: 26 }} onClick={() => actions.removePartner(p.id, t.id)}><Icon.close width={12} /></button>
            </div>
          )}
        </div>
      ))}
      {open && <GiveAccessModal p={p} by={by} onClose={() => setOpen(false)} />}
      <PartnerInbox p={p} canEdit={canEdit} by={by} />
    </div>
  );
}

/* What the partners sent back: design submissions to review, cost proposals to
   answer. Each gets a written response that lands in their workspace. */
function PartnerInbox({ p, canEdit, by }: { p: Project; canEdit: boolean; by: string }) {
  const subs = p.partnerSubmissions ?? [];
  const props = p.partnerProposals ?? [];
  const [answering, setAnswering] = useState<{ kind: "sub" | "prop"; id: string } | null>(null);
  const [resp, setResp] = useState("");
  if (!subs.length && !props.length) return null;

  const answer = (kind: "sub" | "prop", id: string, status: string) => {
    if (!resp.trim()) return;
    if (kind === "sub") actions.respondPartnerSubmission(p.id, id, { status: status as "approved" | "revise", response: resp }, by);
    else actions.respondPartnerProposal(p.id, id, { status: status as "accepted" | "declined", response: resp }, by);
    setAnswering(null); setResp("");
  };

  const Row = ({ r, kind }: { r: any; kind: "sub" | "prop" }) => (
    <div style={{ padding: "13px 18px", borderTop: "1px solid var(--line)" }}>
      <div className="row between wrap" style={{ gap: 10 }}>
        <span className="row wrap" style={{ gap: 8, alignItems: "baseline", minWidth: 0 }}>
          <span className="badge accent mono" style={{ fontSize: 10 }}>{r.ref}</span>
          <b style={{ fontSize: 13.5 }}>{r.title}</b>
          {typeof r.amount === "number" && <span className="mono" style={{ fontSize: 13 }}>{r.amount.toLocaleString()} {p.currency}</span>}
          <span className="dim" style={{ fontSize: 11 }}>{r.partnerName}{r.discipline ? ` · ${r.discipline}` : ""} · {fmtDate(r.on)}</span>
        </span>
        <span className="badge" style={{ fontSize: 8.5, textTransform: "capitalize" }}>{r.status}</span>
      </div>
      {r.note && <div className="dim" style={{ fontSize: 12.5, marginTop: 5, lineHeight: 1.5 }}>{r.note}</div>}
      {(r.files ?? []).length > 0 && (
        <div className="row wrap" style={{ gap: 6, marginTop: 7 }}>
          {r.files.map((x: any, i: number) => <a key={i} className="badge" style={{ fontSize: 10 }} href={x.url} target="_blank" rel="noreferrer">{x.name}</a>)}
        </div>
      )}
      {r.response && <div className="panel" style={{ padding: "8px 12px", marginTop: 8, fontSize: 12.5 }}><b>{r.respondedBy}:</b> {r.response}</div>}
      {canEdit && r.status === "received" && (
        answering?.kind === kind && answering.id === r.id ? (
          <div className="col" style={{ gap: 8, marginTop: 9 }}>
            <textarea className="input" rows={2} value={resp} onChange={(e) => setResp(e.target.value)}
              placeholder={kind === "sub" ? "The studio's review — what's accepted, what must change." : "The studio's answer on the price."} />
            <div className="row wrap" style={{ gap: 8, justifyContent: "flex-end" }}>
              <button className="btn ghost sm" onClick={() => setAnswering(null)}>Cancel</button>
              {kind === "sub" ? (
                <>
                  <button className="btn soft sm" disabled={!resp.trim()} onClick={() => answer(kind, r.id, "revise")}>Return for revision</button>
                  <button className="btn accent sm" disabled={!resp.trim()} onClick={() => answer(kind, r.id, "approved")}><Icon.check width={13} />Approve</button>
                </>
              ) : (
                <>
                  <button className="btn soft sm" disabled={!resp.trim()} onClick={() => answer(kind, r.id, "declined")}>Decline</button>
                  <button className="btn accent sm" disabled={!resp.trim()} onClick={() => answer(kind, r.id, "accepted")}><Icon.check width={13} />Accept</button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 9 }}><button className="chip" style={{ fontSize: 11 }} onClick={() => { setAnswering({ kind, id: r.id }); setResp(""); }}>Respond…</button></div>
        )
      )}
    </div>
  );

  return (
    <>
      {subs.length > 0 && (
        <>
          <div className="row between" style={{ padding: "12px 18px", borderTop: "1px solid var(--line)", background: "color-mix(in srgb, var(--sky) 5%, transparent)" }}>
            <span className="kicker">Design submissions received</span>
            {subs.some((s) => s.status === "received") && <span className="badge accent" style={{ fontSize: 9 }}>{subs.filter((s) => s.status === "received").length} to review</span>}
          </div>
          {subs.map((r) => <Row key={r.id} r={r} kind="sub" />)}
        </>
      )}
      {props.length > 0 && (
        <>
          <div className="row between" style={{ padding: "12px 18px", borderTop: "1px solid var(--line)", background: "color-mix(in srgb, var(--gold) 6%, transparent)" }}>
            <span className="kicker">Cost proposals received</span>
            {props.some((s) => s.status === "received") && <span className="badge accent" style={{ fontSize: 9 }}>{props.filter((s) => s.status === "received").length} to answer</span>}
          </div>
          {props.map((r) => <Row key={r.id} r={r} kind="prop" />)}
        </>
      )}
    </>
  );
}

function GiveAccessModal({ p, by, onClose }: { p: Project; by: string; onClose: () => void }) {
  const allDisc = [...new Set((p.deliverables ?? []).map((d) => d.discipline))];
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [kind, setKind] = useState<"consultant" | "contractor">("consultant");
  const [disc, setDisc] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [made, setMade] = useState<{ token: string; ref: string } | null>(null);
  const link = made ? `https://${STUDIO.domain}/partner/${made.token}` : "";

  return (
    <Modal open onClose={onClose} title={made ? `${made.ref} — access created` : "Give project access"} wide>
      {made ? (
        <div className="col" style={{ gap: 14 }}>
          <div className="panel" style={{ padding: 14, borderLeft: "3px solid var(--accent)" }}>
            <div className="row" style={{ gap: 8, marginBottom: 6 }}><Icon.check width={16} style={{ color: "var(--accent-ink)" }} /><b>{name} can now open the project</b></div>
            <p className="dim" style={{ fontSize: 12.5, marginBottom: 10 }}>{email ? `Emailed to ${email}. ` : ""}The link is personal and can be withdrawn any time.</p>
            <div className="row" style={{ gap: 8 }}>
              <input className="input mono" readOnly value={link} style={{ flex: 1, fontSize: 12 }} onFocus={(e) => e.currentTarget.select()} />
              <button className="btn accent sm" onClick={() => void navigator.clipboard?.writeText(link)}>Copy</button>
            </div>
          </div>
          <div className="row" style={{ justifyContent: "flex-end" }}><button className="btn" onClick={onClose}>Done</button></div>
        </div>
      ) : (
        <div className="col" style={{ gap: 14 }}>
          <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55 }}>They'll see only the drawings for the disciplines you choose, the RFIs that concern them, and the published dates — never fees or the client's details.</p>
          <div className="row" style={{ gap: 12 }}>
            <div className="field" style={{ flex: 1 }}><label>Firm / person</label><input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Meridian Structures" /></div>
            <div className="field" style={{ width: 150 }}><label>Type</label><select className="select" value={kind} onChange={(e) => setKind(e.target.value as any)}><option value="consultant">Consultant</option><option value="contractor">Contractor</option></select></div>
          </div>
          <div className="field"><label>Email <span className="dim">— sends them the link</span></label><input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@…" /></div>
          <div className="field"><label>Which disciplines <span className="dim">— none selected means all</span></label>
            <div className="row wrap" style={{ gap: 6 }}>
              {allDisc.map((d) => <button key={d} className={"chip" + (disc.includes(d) ? " on" : "")} style={{ fontSize: 11.5, background: disc.includes(d) ? "var(--accent)" : "var(--card)", color: disc.includes(d) ? "#fff" : "var(--ink-2)", borderColor: disc.includes(d) ? "var(--accent)" : "var(--line-2)" }} onClick={() => setDisc((x) => x.includes(d) ? x.filter((y) => y !== d) : [...x, d])}>{d}</button>)}
            </div>
          </div>
          <div className="field"><label>Note <span className="dim">(optional)</span></label><textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What they're being asked to do." /></div>
          <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
            <button className="btn ghost" onClick={onClose}>Cancel</button>
            <button className="btn accent" disabled={!name.trim()} onClick={() => { const t = actions.createPartner(p.id, { name, email, kind, disciplines: disc, note, by }); setMade({ token: t.token, ref: t.ref }); }}><Icon.send width={15} />Create access</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
